/**
 * The thinnest possible wrapper over the Clerk script loaded by index.html.
 * Nothing may read the session before `ready()` resolves: until Clerk has
 * loaded, a signed-in person looks signed out.
 *
 * ⚠ `getToken()` with no arguments: the default session token carries sub, sid,
 * email and email_verified. A named JWT template would 404.
 */

interface ClerkSession {
  getToken(): Promise<string | null>;
}

interface ClerkLike {
  loaded?: boolean;
  session?: ClerkSession | null;
  load(opts?: Record<string, unknown>): Promise<void>;
  signOut(): Promise<void>;
  mountSignIn(el: HTMLElement, opts?: Record<string, unknown>): void;
  unmountSignIn(el: HTMLElement): void;
  addListener(cb: (resources: { session?: ClerkSession | null }) => void): () => void;
}

declare global {
  interface Window {
    Clerk?: ClerkLike;
  }
}

/**
 * Local development without a Clerk login.
 *
 * ⚠ Guarded by `import.meta.env.DEV`, which Vite statically replaces with
 * `false` in a production build — so this branch is removed by dead-code
 * elimination and cannot ship enabled, whatever the env var says. Pair it with
 * AUTH_DEV_BYPASS on the API.
 */
export const DEV_BYPASS = import.meta.env.DEV && import.meta.env.VITE_AUTH_DEV_BYPASS === "1";

let loading: Promise<ClerkLike | null> | null = null;

/** Resolve once Clerk's script has loaded and initialised, or null if it never arrives. */
export function ready(): Promise<ClerkLike | null> {
  if (DEV_BYPASS) return Promise.resolve(null);
  if (loading) return loading;
  loading = new Promise((resolve) => {
    const started = Date.now();
    const tick = async () => {
      const clerk = window.Clerk;
      if (clerk) {
        if (!clerk.loaded) await clerk.load().catch(() => {});
        resolve(clerk);
        return;
      }
      if (Date.now() - started > 15_000) {
        resolve(null);
        return;
      }
      setTimeout(tick, 50);
    };
    void tick();
  });
  return loading;
}

export function hasSession(): boolean {
  if (DEV_BYPASS) return true;
  return Boolean(window.Clerk?.session);
}

export async function getToken(): Promise<string | null> {
  if (DEV_BYPASS) return null;
  const clerk = window.Clerk;
  if (!clerk?.loaded || !clerk.session) return null;
  try {
    return await clerk.session.getToken();
  } catch {
    return null;
  }
}

/**
 * ⚠ Global sign-out is a user-initiated action only. The session is shared by
 * every leychoon.app app; the app never calls this as a reflex to a refusal.
 */
export async function signOut(): Promise<void> {
  await window.Clerk?.signOut();
}
